import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { fetchAuthHistory } from './authHistoryService';
import '../process-config/components/CondicoesIntervencaoTableV2.css';
import './AuthHistoryModal.css';

const METHOD_LABEL = {
  password: 'Senha',
  email_code: 'E-mail',
  google: 'Google',
};

function formatDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(value);
  }
}

/** Rótulo curto e legível (sem truncar UA completo). */
function formatBrowser(ua) {
  if (!ua) return '—';
  const s = String(ua);
  let browser = 'Navegador';
  if (/Edg\//i.test(s)) browser = 'Edge';
  else if (/OPR\/|Opera/i.test(s)) browser = 'Opera';
  else if (/Chrome\//i.test(s) && !/Chromium/i.test(s)) browser = 'Chrome';
  else if (/Firefox\//i.test(s)) browser = 'Firefox';
  else if (/Safari\//i.test(s)) browser = 'Safari';

  let os = '';
  if (/Windows NT 10/i.test(s) || /Windows/i.test(s)) os = 'Windows';
  else if (/Mac OS X|Macintosh/i.test(s)) os = 'macOS';
  else if (/Android/i.test(s)) os = 'Android';
  else if (/iPhone|iPad|iOS/i.test(s)) os = 'iOS';
  else if (/Linux/i.test(s)) os = 'Linux';

  return os ? `${browser} · ${os}` : browser;
}

/**
 * @param {{ open: boolean, onClose: () => void, mode?: 'me'|'user', userId?: number|string, title?: string }} props
 */
function AuthHistoryModal({ open, onClose, mode = 'me', userId, title = 'Histórico de acesso' }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setLoading(true);
    setRows([]);
    fetchAuthHistory({ mode, userId, limit: 50 })
      .then((data) => setRows(data))
      .catch((e) => setError(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [open, mode, userId]);

  if (!open) return null;

  return (
    <div className="auth-history-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="auth-history-modal">
        <div className="auth-history-header">
          <h2 className="auth-history-title">{title}</h2>
          <button type="button" className="auth-history-close" onClick={onClose} aria-label="Fechar">
            <X size={18} strokeWidth={2} />
          </button>
        </div>
        <div className="auth-history-body">
          <div className="ci2-root auth-history-ci2" role="region" aria-label={title}>
            <div className="ci2-panel">
              {loading ? (
                <p className="ci2-foot-note auth-history-note">Carregando…</p>
              ) : error ? (
                <p className="ci2-foot-note auth-history-note auth-history-note--err">{error}</p>
              ) : (
                <div className="ci2-table-scroll auth-history-scroll">
                  <table className="ci2-table auth-history-table">
                    <colgroup>
                      <col className="auth-history-col-dt" />
                      <col className="auth-history-col-ip" />
                      <col className="auth-history-col-country" />
                      <col className="auth-history-col-city" />
                      <col className="auth-history-col-browser" />
                      <col className="auth-history-col-method" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Data/hora</th>
                        <th>IP</th>
                        <th>País</th>
                        <th>Cidade</th>
                        <th>Navegador</th>
                        <th>Método</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="auth-history-empty-cell">
                            Nenhum acesso registrado.
                          </td>
                        </tr>
                      ) : (
                        rows.map((row) => (
                          <tr key={row.id} className="ci2-data-row">
                            <td className="auth-history-td-dt">{formatDateTime(row.created_at)}</td>
                            <td className="auth-history-td-ip">{row.ip_public || '—'}</td>
                            <td className="auth-history-td-country">{row.country || '—'}</td>
                            <td className="auth-history-td-city">{row.city || '—'}</td>
                            <td className="auth-history-td-browser">{formatBrowser(row.user_agent)}</td>
                            <td className="auth-history-td-method">{METHOD_LABEL[row.auth_method] || row.auth_method || '—'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthHistoryModal;
